/**
 * Biometric Authentication Utilities using WebAuthn
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Check if WebAuthn is supported
export const isBiometricSupported = () => {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === 'function'
  );
};

// Check if conditional mediation (auto-trigger) is available
export const isConditionalMediationAvailable = async () => {
  if (!isBiometricSupported()) return false;
  
  try {
    if (typeof PublicKeyCredential.isConditionalMediationAvailable === 'function') {
      return await PublicKeyCredential.isConditionalMediationAvailable();
    }
    return false;
  } catch {
    return false;
  }
};

// Check if platform authenticator (fingerprint/face) is available
export const isPlatformAuthenticatorAvailable = async () => {
  if (!isBiometricSupported()) return false;
  
  try {
    const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch {
    return false;
  }
};

// Check if user has biometric enabled
export const checkBiometricStatus = async (username) => {
  try {
    const response = await fetch(`${API}/api/auth/webauthn/check/${username}`);
    if (!response.ok) return { biometric_available: false };
    return await response.json();
  } catch {
    return { biometric_available: false };
  }
};

// Get biometric status for logged-in user
export const getBiometricStatus = async (token) => {
  try {
    const response = await fetch(`${API}/api/auth/webauthn/status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to get status');
    return await response.json();
  } catch {
    return { biometric_enabled: false, credentials_count: 0, devices: [] };
  }
};

// Register biometric credential
export const registerBiometric = async (token, deviceName = 'Mein Gerät') => {
  try {
    // Step 1: Get registration options from server - send current origin
    const optionsResponse = await fetch(`${API}/api/auth/webauthn/register-options`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        device_name: deviceName,
        origin: window.location.origin 
      })
    });
    
    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      throw new Error(error.detail || 'Fehler beim Abrufen der Optionen');
    }
    
    const { options } = await optionsResponse.json();
    const parsedOptions = JSON.parse(options);
    
    // Convert base64url strings to ArrayBuffers
    parsedOptions.challenge = base64urlToBuffer(parsedOptions.challenge);
    parsedOptions.user.id = base64urlToBuffer(parsedOptions.user.id);
    
    if (parsedOptions.excludeCredentials) {
      parsedOptions.excludeCredentials = parsedOptions.excludeCredentials.map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      }));
    }
    
    // Step 2: Create credential using browser API
    const credential = await navigator.credentials.create({
      publicKey: parsedOptions
    });
    
    // Step 3: Send credential to server for verification
    const credentialData = {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64url(credential.response.attestationObject),
      }
    };
    
    if (credential.response.getTransports) {
      credentialData.response.transports = credential.response.getTransports();
    }
    
    const verifyResponse = await fetch(`${API}/api/auth/webauthn/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ credential: credentialData })
    });
    
    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      throw new Error(error.detail || 'Registrierung fehlgeschlagen');
    }
    
    return await verifyResponse.json();
  } catch (error) {
    console.error('Biometric registration error:', error);
    throw error;
  }
};

// Authenticate with biometric
export const authenticateWithBiometric = async (username, useConditional = false) => {
  try {
    // Step 1: Get authentication options from server - include origin
    const optionsResponse = await fetch(`${API}/api/auth/webauthn/login-options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, origin: window.location.origin })
    });
    
    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      throw new Error(error.detail || 'Biometrische Anmeldung nicht verfügbar');
    }
    
    const { options } = await optionsResponse.json();
    const parsedOptions = JSON.parse(options);
    
    // Convert base64url strings to ArrayBuffers
    parsedOptions.challenge = base64urlToBuffer(parsedOptions.challenge);
    
    if (parsedOptions.allowCredentials) {
      parsedOptions.allowCredentials = parsedOptions.allowCredentials.map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      }));
    }
    
    // Step 2: Get credential using browser API
    // Use conditional mediation if available and requested (allows auto-trigger)
    const credentialRequestOptions = {
      publicKey: parsedOptions,
      signal: AbortSignal.timeout(60000) // 60 second timeout
    };
    
    if (useConditional) {
      credentialRequestOptions.mediation = 'conditional';
    }
    
    const credential = await navigator.credentials.get(credentialRequestOptions);
    
    // Step 3: Send credential to server for verification
    const credentialData = {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        authenticatorData: bufferToBase64url(credential.response.authenticatorData),
        signature: bufferToBase64url(credential.response.signature),
        userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null,
      }
    };
    
    const verifyResponse = await fetch(`${API}/api/auth/webauthn/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: credentialData, username, origin: window.location.origin })
    });
    
    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      throw new Error(error.detail || 'Anmeldung fehlgeschlagen');
    }
    
    return await verifyResponse.json();
  } catch (error) {
    console.error('Biometric authentication error:', error);
    throw error;
  }
};

// Disable biometric
export const disableBiometric = async (token) => {
  try {
    const response = await fetch(`${API}/api/auth/webauthn/disable`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Deaktivierung fehlgeschlagen');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Disable biometric error:', error);
    throw error;
  }
};

// Helper functions for base64url encoding/decoding
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLength);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Store username in localStorage for biometric login
export const saveBiometricUsername = (username) => {
  localStorage.setItem('biometric_username', username);
};

export const getBiometricUsername = () => {
  return localStorage.getItem('biometric_username');
};

export const clearBiometricUsername = () => {
  localStorage.removeItem('biometric_username');
};
